import { useEffect, useRef } from 'react';
import { useOnlineStatus } from './use-online-status';
import { useSyncMachineService } from '@/contexts/sync-machine-context';
import { scheduleSync } from '@/stores/sync-orchestrator';
import { getVaultMetadata } from '@/utils/vault-storage';
import type { PausedState } from '@/services/sync-state/types';
import type { AppDispatch } from '@/stores/store';

/**
 * Monitors browser online / offline events and propagates them to all active
 * sync state machines.
 *
 * - Going offline: pauses every machine that is currently in a pausable state.
 * - Coming back online: resumes every paused machine (restoring context from
 *   localStorage metadata) and schedules an immediate sync cycle.
 *
 * Mount this hook in a component that is always mounted while the user is
 * signed in (e.g. the root `App` component).
 *
 * @param dispatch The Redux `AppDispatch` function, used by `scheduleSync`.
 */
export function useSyncPauseResume(dispatch: AppDispatch): void {
  const isOnline = useOnlineStatus();
  const service = useSyncMachineService();

  // Track the previous online state so we only react to *changes*.
  const prevOnlineRef = useRef<boolean | null>(null);

  useEffect(() => {
    // Skip on the very first render — we only want to react to transitions.
    if (prevOnlineRef.current === null) {
      prevOnlineRef.current = isOnline;
      return;
    }

    const wasOnline = prevOnlineRef.current;
    prevOnlineRef.current = isOnline;

    if (wasOnline === isOnline) return;

    if (!isOnline) {
      // ── Device went offline ───────────────────────────────────────────────
      console.log('[useSyncPauseResume] Device offline — pausing all active machines');
      for (const [vaultId, machine] of service.listAll()) {
        try {
          machine.pause();
          console.log(`[useSyncPauseResume] Paused machine for vault ${vaultId}`);
        } catch {
          // Machine is not in a pausable state (e.g. local-only, error) — skip
        }
      }
    } else {
      // ── Device came back online ───────────────────────────────────────────
      console.log('[useSyncPauseResume] Device online — resuming paused machines');
      for (const [vaultId, machine] of service.listAll()) {
        if (machine.status !== 'paused') continue;

        const pausedState = machine.state as PausedState;
        const { localId, serverId } = pausedState;

        // serverId is required to resume a sync-enabled vault
        if (!serverId) {
          // No serverId — this was a local-only vault that somehow got paused
          // (shouldn't happen, but be defensive)
          continue;
        }

        const meta = getVaultMetadata(localId);
        if (!meta?.serverLastModified || !meta?.lastSyncDate) {
          console.warn(
            `[useSyncPauseResume] Cannot resume vault ${vaultId} — missing metadata`,
          );
          continue;
        }

        machine.resume({
          localId,
          serverId,
          serverLastModified: meta.serverLastModified,
          lastSyncDate: meta.lastSyncDate,
        });
        console.log(`[useSyncPauseResume] Resumed machine for vault ${vaultId}`);

        // Trigger a sync cycle now that we're back online
        scheduleSync(vaultId, dispatch);
      }
    }
  }, [isOnline, service, dispatch]);
}
